import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FileText, FileEdit, Briefcase, Pencil, Trash2 } from 'lucide-react';
import SectionHeader from './SectionHeader';
import ATSCheckButton from '@/components/ATSCheckButton';
import { formatResumeForATS } from '@/utils/resumeFormatter';

const ResumesSection = ({ 
  resumes, 
  sectionOrder, 
  onMoveSectionUp, 
  onMoveSectionDown, 
  onDeleteResume,
  onCreateClick,
  isVisible 
}) => {
  const navigate = useNavigate();

  if (!isVisible) return null;

  return (
    <div className="mb-12">
      <SectionHeader 
        title="Your Resumes" 
        sectionId="resumes"
        viewAllPath={resumes.length > 0 ? "/resumes" : null}
        viewAllColor="sky"
        showCreateButton={true}
        onCreateClick={onCreateClick}
        sectionOrder={sectionOrder}
        onMoveSectionUp={onMoveSectionUp}
        onMoveSectionDown={onMoveSectionDown}
      />
      {resumes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200" data-testid="no-resumes-message">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No resumes yet. Create your first one!</p>
          <Button onClick={onCreateClick} className="bg-sky-600 hover:bg-sky-700 text-white">
            Get Started
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {resumes.slice(0, 3).map((resume) => (
            <div
              key={resume.id}
              className="p-6 bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow"
              data-testid={`resume-card-${resume.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-sky-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(resume.updated_at).toLocaleDateString()}
                </span>
              </div>
              <h3 className="font-semibold text-lg mb-2">{resume.title}</h3>
              <p className="text-sm text-gray-600 mb-4">
                Updated {new Date(resume.updated_at).toLocaleDateString()}
              </p>
              <div className="space-y-2">
                {resume.type === 'visual' ? (
                  <>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/resume/visual-builder', { state: { resumeId: resume.id, isVisual: true } });
                      }}
                      data-testid={`edit-resume-${resume.id}`}
                      className="bg-sky-600 hover:bg-sky-700 text-white w-full"
                    >
                      <FileEdit className="w-3 h-3 mr-1" />
                      Standard Editor
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <ATSCheckButton
                        resumeContent={formatResumeForATS(resume.sections)}
                        resumeId={resume.id}
                        buttonText="ATS Check"
                        variant="outline"
                        size="sm"
                        className="border-green-200 text-green-600 hover:bg-green-50"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteResume(resume.id, resume.type);
                        }}
                        data-testid={`delete-resume-${resume.id}`}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/resume/${resume.id}`);
                        }}
                        data-testid={`edit-resume-${resume.id}`}
                        className="bg-sky-600 hover:bg-sky-700 text-white"
                      >
                        <FileEdit className="w-3 h-3 mr-1" />
                        Standard Editor
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/resume/visual-builder', { 
                            state: { 
                              resumeId: resume.id,
                              isVisual: false
                            } 
                          });
                        }}
                        className="border-purple-200 text-purple-600 hover:bg-purple-50"
                      >
                        <Briefcase className="w-3 h-3 mr-1" />
                        Visual Editor
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <ATSCheckButton
                        resumeContent={formatResumeForATS(resume.sections)}
                        resumeId={resume.id}
                        buttonText="ATS Check"
                        variant="outline"
                        size="sm"
                        className="border-green-200 text-green-600 hover:bg-green-50"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteResume(resume.id, resume.type);
                        }}
                        data-testid={`delete-resume-${resume.id}`}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResumesSection;
